/**
 * Sync engine: drains the outbox to POST /sync/commands in the order commands were made.
 *
 *  triggers  : enqueue (debounced) · `online` · tab becomes visible · interval · SW Background Sync
 *              message · manual "Sync now"
 *  per batch : send → apply per-command results; photos follow in their own short round (or, with
 *              `mediaBeforeCommand`, go first and hold their command until they arrive)
 *  failures  : network/5xx/429 → backoff; 401 → stop, "sign in again"; batch-level 400/409 →
 *              resend one by one to find the bad command, which alone goes to ERROR
 *  revoke    : reply says device REVOKED → wipe roster + PIN checks, keep sending the outbox
 */
import { ApiError } from '@/api/errors'
import { getDb, type OutboxRecord, type SupplyChainDB } from './db'
import { getMeta, setMeta } from './meta'
import {
  applyPatches,
  autoRetryRefused,
  loadOpenRecords,
  markSending,
  onEnqueue,
  planResultUpdates,
  planTransientFailure,
  recoverInFlight,
  selectBatch,
} from './outbox'
import { mediaNotUploaded, recoverStuckMedia, uploadMedia } from './media'
import { SYNC_PROTOCOL_VERSION, toWireCommand, type SyncCommandsResponse } from './protocol'
import { httpTransport, type SyncTransport } from './transport'

/** One round of photo uploads stops starting new photos after this; the rest go next round. */
export const PHOTO_ROUND_MS = 90_000

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error' | 'auth_required'

export interface SyncState {
  phase: SyncPhase
  lastSyncedAt: number | null
  lastError: string | null
}

export interface SyncEngineOptions {
  db?: SupplyChainDB
  transport?: SyncTransport
  now?: () => number
  random?: () => number
  /** Periodic drain while the app is open. */
  intervalMs?: number
  /** Device id for the envelope. */
  getDeviceId?: () => Promise<string | null>
  /** Called when the server says this device was revoked. */
  onRevoked?: () => Promise<void>
  /** Upload a command's photos before sending it (default) or let them follow. */
  mediaBeforeCommand?: boolean
  /** False when there is no online session to send with — the engine then waits for a sign-in. */
  hasCredentials?: () => boolean
  /** After a drain that delivered commands (e.g. refresh the store list). Errors are ignored. */
  onDelivered?: () => Promise<unknown>
}

type Listener = (s: SyncState) => void
export const BACKGROUND_SYNC_TAG = 'uc-outbox-sync'
export const SW_SYNC_MESSAGE = 'UC_SYNC_REQUESTED'

/**
 * Only one tab of this app sends at a time. Several open tabs each run an engine against the same
 * storage and the same network: on an iPhone that is how the storage gets stuck. A tab that finds
 * another one sending skips this round (that tab sends everything anyway).
 */
export const SYNC_LOCK = 'uc-outbox-sync'

async function withSyncLock(run: () => Promise<void>): Promise<void> {
  const locks = (typeof navigator !== 'undefined' ? navigator.locks : undefined) as
    LockManager | undefined
  if (!locks?.request) return run() // older browsers: as before
  await locks.request(SYNC_LOCK, { ifAvailable: true }, async (lock) => {
    if (lock) await run()
  })
}

/**
 * Ask the browser to keep this app's data. Without it a phone short of space (iPhones especially)
 * may delete IndexedDB — unsent purchases and photos with it. Best effort: some browsers grant it
 * silently, some only for installed apps.
 */
export async function keepStorage(): Promise<boolean> {
  try {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined
    if (!storage?.persist) return false
    if (await storage.persisted?.()) return true
    return await storage.persist()
  } catch {
    return false
  }
}

export class SyncEngine {
  private readonly db: SupplyChainDB
  private readonly transport: SyncTransport
  private readonly now: () => number
  private readonly random: () => number
  private readonly opts: SyncEngineOptions
  private state: SyncState = { phase: 'idle', lastSyncedAt: null, lastError: null }
  private listeners = new Set<Listener>()
  private running: Promise<void> | null = null
  private rerun = false
  private cleanup: Array<() => void> = []
  private debounce: ReturnType<typeof setTimeout> | null = null

  constructor(opts: SyncEngineOptions = {}) {
    this.opts = opts
    this.db = opts.db ?? getDb()
    this.transport = opts.transport ?? httpTransport
    this.now = opts.now ?? Date.now
    this.random = opts.random ?? Math.random
  }

  // ---------- state ----------

  getState(): SyncState {
    return this.state
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }

  private setState(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((l) => l(this.state))
  }

  // ---------- lifecycle ----------

  /** Wire up triggers. Idempotent; returns a stop function. */
  async start(): Promise<() => void> {
    if (this.cleanup.length) return () => this.stop()
    await recoverInFlight(this.db)
    await recoverStuckMedia(this.db)
    void keepStorage()
    const last = await getMeta('last_sync_at', this.db)
    this.setState({ lastSyncedAt: last ?? null })

    const kick = () => void this.syncNow()
    const onVisible = () => document.visibilityState === 'visible' && kick()
    window.addEventListener('online', kick)
    document.addEventListener('visibilitychange', onVisible)
    const timer = setInterval(kick, this.opts.intervalMs ?? 30_000)
    const offEnqueue = onEnqueue(() => {
      if (this.debounce) clearTimeout(this.debounce)
      this.debounce = setTimeout(kick, 400)
      void this.requestBackgroundSync()
    })
    const onSwMessage = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type === SW_SYNC_MESSAGE) kick()
    }
    navigator.serviceWorker?.addEventListener('message', onSwMessage)

    this.cleanup = [
      () => window.removeEventListener('online', kick),
      () => document.removeEventListener('visibilitychange', onVisible),
      () => clearInterval(timer),
      offEnqueue,
      () => navigator.serviceWorker?.removeEventListener('message', onSwMessage),
      () => this.debounce && clearTimeout(this.debounce),
    ]
    kick()
    return () => this.stop()
  }

  stop(): void {
    this.cleanup.forEach((fn) => fn())
    this.cleanup = []
  }

  /** Ask the browser to wake the service worker when connectivity returns (Chrome/Android). */
  async requestBackgroundSync(): Promise<void> {
    try {
      const reg = await navigator.serviceWorker?.ready
      const sync = (
        reg as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }
      )?.sync
      await sync?.register(BACKGROUND_SYNC_TAG)
    } catch {
      /* unsupported or denied — interval and online triggers still cover it */
    }
  }

  /** Drain now. Concurrent calls coalesce into one run plus at most one follow-up. */
  syncNow(): Promise<void> {
    if (this.running) {
      this.rerun = true
      return this.running
    }
    this.running = (async () => {
      try {
        await withSyncLock(async () => {
          do {
            this.rerun = false
            await this.drain()
          } while (this.rerun && this.state.phase === 'idle')
        })
      } finally {
        this.running = null
      }
    })()
    return this.running
  }

  // ---------- core ----------

  private async drain(): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setState({ phase: 'offline' })
      return
    }
    if (this.opts.hasCredentials && !this.opts.hasCredentials()) {
      const open = await loadOpenRecords(this.db)
      const waiting = open.some((r) => r.status !== 'SET_ASIDE')
      this.setState({ phase: waiting ? 'auth_required' : 'idle' })
      return
    }
    const deviceId =
      (await this.opts.getDeviceId?.()) ??
      (await getMeta('device', this.db))?.id ??
      (await getMeta('device_uid', this.db)) ??
      null
    if (!deviceId) return

    await autoRetryRefused(this.now(), this.db)
    let maxBatch: number | undefined
    let sentAny = false
    this.setState({ phase: 'syncing' })

    for (;;) {
      const open = await loadOpenRecords(this.db)
      let batch = selectBatch(open, this.now(), maxBatch)
      if (batch.length === 0) break

      if (this.opts.mediaBeforeCommand !== false) {
        batch = await this.uploadBatchMedia(batch)
        if (batch.length === 0) {
          this.setState({ phase: 'error', lastError: 'Photos could not be uploaded; will retry' })
          return
        }
      }

      const outcome = await this.sendBatch(deviceId, batch)
      if (outcome === 'isolate') {
        maxBatch = 1
        continue
      }
      if (outcome !== 'ok') return // state already set
      sentAny = true
    }

    const syncedAt = this.now()
    if (sentAny) {
      await setMeta('last_sync_at', syncedAt, this.db)
      void this.opts.onDelivered?.().catch(() => undefined)
    }
    // The records are on the server: say so now, not after the photos (which can take a while).
    this.setState({
      phase: 'idle',
      lastError: null,
      ...(sentAny ? { lastSyncedAt: syncedAt } : {}),
    })

    // Photos, in one short round: a weak link ends it early and the next round carries on.
    await uploadMedia(
      (rec) => this.transport.uploadMedia(rec),
      { now: this.now(), random: this.random, deadline: Date.now() + PHOTO_ROUND_MS },
      this.db,
    )
  }

  /** Upload photos for the batch; cut the batch before the first command whose photos failed. */
  private async uploadBatchMedia(batch: OutboxRecord[]): Promise<OutboxRecord[]> {
    const ids = [...new Set(batch.flatMap((r) => r.media_ids))]
    if (ids.length === 0) return batch
    const missing = new Set(await mediaNotUploaded(ids, this.db))
    if (missing.size === 0) return batch
    await uploadMedia(
      (rec) => this.transport.uploadMedia(rec),
      {
        ids: [...missing],
        now: this.now(),
        random: this.random,
        deadline: Date.now() + PHOTO_ROUND_MS,
      },
      this.db,
    )
    const stillMissing = new Set(await mediaNotUploaded([...missing], this.db))
    const cut = batch.findIndex((r) => r.media_ids.some((m) => stillMissing.has(m)))
    return cut === -1 ? batch : batch.slice(0, cut)
  }

  private async sendBatch(
    deviceId: string,
    loaded: OutboxRecord[],
  ): Promise<'ok' | 'isolate' | 'stop'> {
    const marked = new Set(
      await markSending(
        loaded.map((r) => r.id),
        this.db,
      ),
    )
    // A record set aside since the batch was loaded is not sent (nor are its children: aside too).
    const batch = loaded.filter((r) => marked.has(r.id))
    if (batch.length === 0) return 'ok'
    let res: SyncCommandsResponse
    try {
      res = await this.transport.sendCommands({
        device_id: deviceId,
        protocol_version: SYNC_PROTOCOL_VERSION,
        sent_at: new Date(this.now()).toISOString(),
        commands: batch.map(toWireCommand),
      })
    } catch (e) {
      return this.handleTransportError(e, batch)
    }

    await applyPatches(planResultUpdates(batch, res.results, this.now(), this.random), this.db)
    if (res.server_time) {
      const server = Date.parse(res.server_time)
      if (!Number.isNaN(server))
        await setMeta('server_clock', { server, local: this.now() }, this.db)
    }
    if (res.device?.status === 'REVOKED' || res.device?.wipe) {
      await this.opts.onRevoked?.()
    }
    return 'ok'
  }

  private async handleTransportError(
    e: unknown,
    batch: OutboxRecord[],
  ): Promise<'isolate' | 'stop'> {
    const err =
      e instanceof ApiError
        ? e
        : new ApiError({
            kind: 'network',
            message: e instanceof Error ? e.message : 'Sync failed',
            cause: e,
          })
    const now = this.now()

    // 401 after refresh failed: keep everything as it was, wait for a person to sign in.
    if (err.kind === 'unauthorized') {
      await applyPatches(new Map(batch.map((r) => [r.id, { status: 'PENDING' as const }])), this.db)
      this.setState({ phase: 'auth_required', lastError: err.message })
      return 'stop'
    }

    // The whole request was refused: find the bad command by sending one at a time.
    if ((err.kind === 'validation' || err.kind === 'conflict') && batch.length > 1) {
      await applyPatches(new Map(batch.map((r) => [r.id, { status: 'PENDING' as const }])), this.db)
      return 'isolate'
    }
    if ((err.kind === 'validation' || err.kind === 'conflict') && batch.length === 1) {
      const result = err.kind === 'conflict' ? 'CONFLICT' : 'REJECTED_MALFORMED'
      await applyPatches(
        planResultUpdates(
          batch,
          [
            {
              id: batch[0]!.id,
              result,
              exception_ids: [],
              error: { code: err.code, message: err.message },
            },
          ],
          now,
          this.random,
        ),
        this.db,
      )
      return 'isolate' // keep draining the rest one by one
    }

    await applyPatches(
      planTransientFailure(
        batch,
        err.message,
        err.code ?? err.kind.toUpperCase(),
        now,
        this.random,
      ),
      this.db,
    )
    this.setState({
      phase: err.kind === 'network' || err.kind === 'timeout' ? 'offline' : 'error',
      lastError: err.message,
    })
    return 'stop'
  }
}
